import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 마이페이지 연차 (DOC-47 §3.2 · `GET /api/v1/mypage/leave`).
 *
 * **화면이 세는 것은 없다.** 의무사용 미달분·수당 대상 일수·소멸까지 서버가 답한다 — 같은 셈을
 * 두 곳에서 하면 두 숫자가 갈리고, 그때 어느 쪽이 맞는지 아무도 모른다. 여기서는 받은 값을
 * 배치할 뿐이다.
 *
 * 홈 위젯이 쓰는 `useHrProfile` 과 다른 경로다. 그쪽은 "잔여 몇 일" 한 줄이면 되는 위젯용 요약이고,
 * 이 화면은 기간·계획·촉진까지 본다 — 위젯에 이 응답을 통째로 실으면 홈이 무거워진다.
 */

export type LeaveStandard = {
  /** 반드시 써야 하는 일수(사내 지침). 기준표가 없으면 null 이다. */
  mandatoryUseDays: number | null;
  allowanceOverMandatory: boolean;
  /** 인사팀이 확정했는가. false 면 화면이 「확정 전 잠정값」 이라고 적는다. */
  confirmed: boolean;
  accrualBasis: string | null;
  guidelinePostId: string | null;
};

export type LeavePeriod = {
  startDate: string;
  endDate: string;
  expiresOn: string;
  grantedDays: number;
  usedDays: number;
  remainingDays: number;
  mandatoryDays: number | null;
  /** 아직 반드시 써야 하는 일수. */
  mandatoryShortfallDays: number | null;
  /** 수당 산정 대상 일수. **금액은 오지 않는다** — 통상임금이 시스템에 없다(DOC-47 §10-2). */
  allowanceDays: number | null;
  daysUntilExpiry: number;
  expired: boolean;
  /** 출근율을 확인하지 못한 부여가 섞여 있는가. */
  assumption: boolean;
};

export type LeaveUsage = {
  startDate: string;
  endDate: string;
  days: number;
  leaveType: string;
  reason: string | null;
  /** 연차 잔여에서 빠지는가. 경조사·병가는 빠지지 않는다. */
  deducted: boolean;
  fromApproval: boolean;
  /** 연차 취소 신청이 더한 음수 줄(V544). 서버 재시작 전에는 오지 않는다. */
  cancel?: boolean;
};

export type LeavePlan = { planDate: string; days: number; fulfilled: boolean };

export type LeavePromotion = {
  stage: 'BULK' | 'FIRST' | 'SECOND';
  stageLabel: string;
  notifiedOn: string;
  periodStart: string;
  periodEnd: string;
  remainingDays: number;
  responded: boolean;
};

/**
 * 올해 회계 기준 「계 · 사용 · 잔여」 — 연차관리 표와 같은 숫자(DOC-113). 입사일을 모르면 total·remaining 이 null.
 */
export type LeaveFiscal = {
  year: number;
  priorYearUsed: number;
  accrued: number | null;
  total: number | null;
  usedDays: number;
  remainingDays: number | null;
};

export type MyLeave = {
  standard: LeaveStandard;
  /** 입사일 기준 연차연도 — 소멸일·의무사용은 이 값이다. */
  current: LeavePeriod | null;
  periods: LeavePeriod[];
  usages: LeaveUsage[];
  plans: LeavePlan[];
  promotions: LeavePromotion[];
  /** ★ 서버 재시작 전에는 없다 — 그때는 current 의 숫자로 물러선다. */
  fiscal?: LeaveFiscal | null;
};

export type LeaveCalendarDay = {
  date: string;
  days: number;
  leaveType: string | null;
  /**
   * 계획 줄에만 뜻이 있다 — <b>그날 실제로 쉬었는가</b>(서버 판정). 실적 줄은 언제나 false 다.
   *
   * <p>화면이 계획 날짜와 사용 날짜를 맞춰 보지 않는 이유: 사용은 <b>일수 규칙</b>으로 펼쳐져
   * 오므로 주말이 빠져 있다. 주말 계획을 허용한 뒤로는 그 교집합이 「이행했는데 미이행」 을 만든다.
   */
  fulfilled: boolean;
  /** 실적 줄에만 — 연차 취소 신청으로 취소된 날(V544). 그날의 실적 줄은 따로 오지 않는다. */
  cancelled?: boolean;
};

export type LeaveCalendar = { year: number; plans: LeaveCalendarDay[]; usages: LeaveCalendarDay[] };

export function useMyLeave(): UseQueryResult<MyLeave> {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: ['mypage-leave', userId],
    queryFn: () => apiFetch<MyLeave>('/v1/mypage/leave'),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

/**
 * 그 해의 휴무 계획을 <b>통째로</b> 저장한다(`PUT /v1/mypage/leave/plans` · 사용자 지시 2026-08-26).
 *
 * <p>결재를 거치지 않는다 — 계획은 승인받는 것이 아니라 언제 쉴지 알리는 값이다. 잔여를 깎는
 * 실제 사용(연차 신청)은 그대로 결재를 받는다.
 *
 * <p>보낸 목록이 그 해의 전부다. 빈 목록은 「계획을 지웠다」 는 뜻이라, 화면은 저장 전에 그 사실을
 * 한 번 더 말한다.
 *
 * <p>성공하면 <b>연차 화면 셋을 모두</b> 무르게 만든다 — 마이페이지 연차 탭·연 캘린더·휴무 관리 표가
 * 같은 계획을 본다. 한 곳만 갱신하면 방금 저장한 사람이 다른 탭에서 옛 계획을 보고 다시 저장한다.
 */
export function useSaveLeavePlans() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { year: number; dates: Array<{ date: string; days: number }> }) =>
      apiFetch<LeaveCalendar>('/v1/mypage/leave/plans', { method: 'PUT', body: input }),
    onSuccess: () => {
      // 캐시에 직접 써넣지 않는다 — 이 키에는 사용자 id 가 섞여 있어, 여기서 지어 넣으면
      // 아무도 읽지 않는 줄이 하나 생기고 화면은 여전히 옛 값을 본다.
      void queryClient.invalidateQueries({ queryKey: ['mypage-leave-calendar'] });
      void queryClient.invalidateQueries({ queryKey: ['mypage-leave'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.leaveBoard });
    },
  });
}

/**
 * 곧 다가오는데 <b>아직 연차 신청을 올리지 않은</b> 계획 (V132).
 *
 * <p>계획은 결재가 아니라 그냥 저장된다. 가벼워진 대신 신청과 끊겨서, 달력에 찍어 두고 정작
 * 결재를 안 올리는 자리가 생겼다 — 그 사이를 이 목록과 홈 할일이 메운다.
 */
export type UpcomingLeavePlan = {
  startDate: string;
  endDate: string;
  days: number;
  /** 오늘로부터 며칠 뒤인가. 0 이면 오늘 — 화면이 오늘을 다시 세면 자정 무렵에 서버와 어긋난다. */
  dDay: number;
  /**
   * `PENDING` 아직 안 올림 · `IN_PROGRESS` 결재 진행 중 (사용자 지시 2026-08-26).
   *
   * <p>결재함에서 직접 기안한 연차도 상신 순간 계획과 이어진다. 그 전까지는 결재가 도는 며칠 내내
   * 「아직 신청을 올리지 않았습니다」 가 떴다 — 이미 올린 사람에게 또 올리라고 조르는 화면이었다.
   */
  status: 'PENDING' | 'IN_PROGRESS';
  /** 진행 중인 문서. `PENDING` 이면 null 이다. */
  documentId: string | null;
};

export function useUpcomingLeavePlans(): UseQueryResult<UpcomingLeavePlan[]> {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: ['mypage-leave-upcoming', userId],
    queryFn: () => apiFetch<UpcomingLeavePlan[]>('/v1/mypage/leave/plans/upcoming'),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export type LeavePlanApplyResult = {
  documentId: string;
  startDate: string;
  endDate: string;
  days: number;
};

/**
 * 그 계획으로 <b>연차 기안을 만들고 상신한다</b>.
 *
 * <p>자동이라도 결재는 그대로 받는다 — 상신까지가 이 요청의 일이고, 잔여는 결재가 완료될 때 깎인다.
 * 성공하면 계획·할일·결재함이 함께 무르다: 방금 올린 신청이 세 화면 중 한 곳에만 보이면 안 된다.
 */
export function useApplyLeavePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { startDate: string; endDate: string; days?: number }) =>
      apiFetch<LeavePlanApplyResult>('/v1/mypage/leave/plans/apply', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mypage-leave-upcoming'] });
      void queryClient.invalidateQueries({ queryKey: ['mypage-leave'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.todoItems });
      // 결재 쪽은 키 앞머리가 `approval` 인 것을 통째로 무르게 한다 — 방금 상신한 문서가
      // 상신함·대기함 어디에 서는지는 결재선이 정하고, 그것을 화면이 미리 알 수 없다.
      void queryClient.invalidateQueries({ queryKey: ['approval'] });
    },
  });
}

/** 연 캘린더. 해가 바뀌면 다른 요청이라 키에 해를 넣는다. */
export function useLeaveCalendar(year: number): UseQueryResult<LeaveCalendar> {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: ['mypage-leave-calendar', userId, year],
    queryFn: () => apiFetch<LeaveCalendar>(`/v1/mypage/leave/calendar?year=${year}`),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

// ── 연차 사용 동의서 (V420) ──────────────────────────────────────────────────

/** 인사총무팀이 요청한 동의서 한 장. `signedAt` 이 비어 있으면 아직 서명하지 않았다. */
export type LeaveConsent = {
  id: string;
  year: number;
  title: string | null;
  body: string | null;
  requestedAt: string;
  signedAt: string | null;
  signatureImage: string | null;
};

/**
 * 내게 온 동의서들(`GET /v1/mypage/leave/consents`).
 *
 * <p>★ 서버가 재시작 전이면 이 경로가 없다 — 실패를 화면 오류로 올리지 않고 빈 목록으로 본다.
 */
export function useMyLeaveConsents() {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: ['mypage-leave-consents', userId],
    queryFn: () => apiFetch<LeaveConsent[]>('/v1/mypage/leave/consents'),
    enabled: !!userId,
    retry: false,
  });
}

/** 등록해 둔 서명으로 서명한다. 연차관리 표의 「동의서」 칸도 같은 줄을 본다. */
export function useSignLeaveConsent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<LeaveConsent>(`/v1/mypage/leave/consents/${id}/sign`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mypage-leave-consents'] });
      void queryClient.invalidateQueries({ queryKey: ['hr-leave-sheet'] });
    },
  });
}
